import { useMemo } from 'react'
import { ColorRepresentation, Vector3 } from 'three'
import { useTheme } from '@mui/material'
import { useElementIdToAssembly, useElementLabel, usePlanLabelPdfMetrics } from '@editor/hooks'
import useIsPositionPlanPreview from '@editor/pages/PlanView/hooks/useIsPositionPlanPreview'
import useIsStiffeningPlanPreview from '@editor/pages/PlanView/hooks/useIsStiffeningPlanPreview'
import { useStiffeningStore } from '@editor/stores'
import { getStiffeningSegmentPlanAxis, isPlanStiffeningSegment } from '@editor/utils'
import { calculateStructuralAssemblyThickness } from 'src/components/manager/assemblies/utils'
import { ElementLabelDescriptor } from 'src/components/scene/ModelElementLabels/types'
import {
  BEAM_LABEL_BORDER_WIDTH,
  STIFFENING_PLAN_WALL_LABEL_COLOR,
  STIFFENING_SEGMENT_LABEL_COLOR,
} from './constants'
import {
  getBeamLabelCenter,
  getColumnLabelCenter,
  getPurlinLabelCenter,
  getSlabLabelCenter,
  getStiffeningSegmentLabelPlacement,
  getStoreyTopZLookup,
  getWallLabelPlacement,
} from './labelPlacements'
import { getPdfLabelBoxDescriptorProps } from './pdfLabelBox'
import { makePlanMemberLabelLines, makePlanSlabLabelLines } from './planLabelText'

/**
 * The floorplan is always viewed top down, so its labels lie flat in the XY
 * plane unless they are aligned with an element.
 */
const PLAN_X_DIRECTION = new Vector3(1, 0, 0)
const PLAN_Y_DIRECTION = new Vector3(0, 1, 0)

/** Vertical labels read bottom-up, like the PDF plan's 90° rotated labels. */
const PLAN_VERTICAL_X_DIRECTION = new Vector3(0, 1, 0)
const PLAN_VERTICAL_Y_DIRECTION = new Vector3(-1, 0, 0)

/** flat text, so there is nothing to extrude */
const FLAT_TEXT_DEPTH = 0

interface Params {
  visibleModel: PlanarModel
  guidToColor: Record<string, ColorRepresentation>
  guidToHidden: Record<string, boolean>
  guidToCrossSection: Record<string, CrossSection>
  showInnerWallLabels: boolean
  showOuterWallLabels: boolean
  showBeamLabels: boolean
  showVerticalSlabLabels: boolean
  showVerticalRoofSlabLabels: boolean
  showPurlinLabels: boolean
  showColumnLabels: boolean
  /** Add the cross section as a second label line, like the plan PDF export */
  showCrossSections: boolean
}

/**
 * Floorplan wall, beam, purlin, column and slab position labels as batchable
 * descriptors.
 *
 * Sizes and default positions mirror the plan PDF export (`make_label_V2` and
 * the elements' `planLabelPositionWorld`), so what the engineer arranges in
 * the scene is what the exported plan shows.
 */
const useFloorplanLabelDescriptors = ({
  visibleModel,
  guidToColor,
  guidToHidden,
  guidToCrossSection,
  showInnerWallLabels,
  showOuterWallLabels,
  showBeamLabels,
  showVerticalSlabLabels,
  showVerticalRoofSlabLabels,
  showPurlinLabels,
  showColumnLabels,
  showCrossSections,
}: Params): ElementLabelDescriptor[] => {
  const { scenePalette } = useTheme()
  const getLabel = useElementLabel()
  const metrics = usePlanLabelPdfMetrics()
  const elementIdToAssembly = useElementIdToAssembly()
  // The stiffening plan labels walls with their assembly (`labelConfig.stiffeningPlan`:
  // assembly only) in black, and adds the stiffening segment labels.
  const isStiffeningPlan = useIsStiffeningPlanPreview()
  // The position plan preview prints each element's lines as its PDF plan settings say.
  const isPositionPlan = useIsPositionPlanPreview()
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const storeyTopZ = useMemo(() => getStoreyTopZLookup(visibleModel), [visibleModel])

  const wallDescriptors = useMemo(() => {
    if (!showInnerWallLabels && !showOuterWallLabels) return []

    return visibleModel.walls.reduce((collector: ElementLabelDescriptor[], wall) => {
      if (guidToHidden[wall.guid]) return collector

      // inner and outer wall labels have independent visibility toggles
      const isExternal = wall.placement === 'External'
      const isInternal = wall.placement === 'Internal'
      if (isExternal ? !showOuterWallLabels : !isInternal || !showInnerWallLabels) return collector

      const color = (guidToColor[wall.guid] ||
        (isStiffeningPlan
          ? STIFFENING_PLAN_WALL_LABEL_COLOR
          : isExternal
          ? scenePalette.elements2d.externalWalls.color
          : scenePalette.elements2d.internalWalls.color)) as string

      const assembly = elementIdToAssembly[wall.guid]
      // a wall without an assembly has no stiffening plan label
      if (isStiffeningPlan && !assembly?.short_name) return collector
      const placement = getWallLabelPlacement(
        wall,
        assembly && calculateStructuralAssemblyThickness(assembly),
        metrics,
      )
      if (!placement) return collector

      const textLines = [isStiffeningPlan ? (assembly as Assembly).short_name : getLabel(wall.guid)]

      collector.push({
        guid: wall.guid,
        textLines,
        ...placement,
        // wall labels are borderless in the PDF, which narrows their box
        ...getPdfLabelBoxDescriptorProps(textLines, metrics, false),
        color,
        opacity: 1,
        textDepth: FLAT_TEXT_DEPTH,
        // The stiffening plan's wall label has no background in the PDF, but the box is what
        // makes a label clickable (and so movable) — white on the white paper it is invisible.
        backgroundColor: '#FFFFFF',
      })

      return collector
    }, [])
  }, [
    isStiffeningPlan,
    showInnerWallLabels,
    showOuterWallLabels,
    visibleModel.walls,
    guidToHidden,
    guidToColor,
    scenePalette.elements2d.externalWalls.color,
    scenePalette.elements2d.internalWalls.color,
    elementIdToAssembly,
    metrics,
    getLabel,
  ])

  // Segment labels of the shown walls. They follow the wall label toggles, since the stiffening
  // plan draws both. Centered on the segment, unless moved (like the export).
  const stiffeningSegmentDescriptors = useMemo(() => {
    if (!isStiffeningPlan) return []

    const wallsByGuid = new Map(visibleModel.walls.map(wall => [wall.guid, wall]))
    return stiffeningIntervals.reduce((collector: ElementLabelDescriptor[], segment) => {
      if (!isPlanStiffeningSegment(segment)) return collector
      const wall = wallsByGuid.get(segment.element_guid)
      if (!wall || guidToHidden[wall.guid]) return collector
      const isExternal = wall.placement === 'External'
      if (isExternal ? !showOuterWallLabels : !showInnerWallLabels) return collector

      const axis = getStiffeningSegmentPlanAxis(wall, segment)
      if (!axis) return collector

      const guid = segment.guid as string
      const textLines = [getLabel(guid)]
      collector.push({
        guid,
        textLines,
        ...getStiffeningSegmentLabelPlacement(axis, storeyTopZ[wall.storey]),
        // borderless and without a background in the PDF (`stiffening_segment_label`)
        ...getPdfLabelBoxDescriptorProps(textLines, metrics, false),
        color: STIFFENING_SEGMENT_LABEL_COLOR,
        opacity: 1,
        textDepth: FLAT_TEXT_DEPTH,
      })
      return collector
    }, [])
  }, [
    isStiffeningPlan,
    stiffeningIntervals,
    visibleModel,
    guidToHidden,
    showInnerWallLabels,
    showOuterWallLabels,
    getLabel,
    metrics,
    storeyTopZ,
  ])

  const beamDescriptors = useMemo(() => {
    if (!showBeamLabels) return []

    return visibleModel.beams.reduce((collector: ElementLabelDescriptor[], beam) => {
      const { start, end } = beam.shape

      // The export snaps beam labels to the page axes (`beamV2.isVertical`):
      // only exactly vertical beams read vertically, diagonals horizontally.
      const isVertical = start.x === end.x

      const color = (guidToColor[beam.guid] || scenePalette.elements2d.beams.color) as string
      const textLines = makePlanMemberLabelLines(
        getLabel(beam.guid),
        guidToCrossSection[beam.guid],
        showCrossSections,
        isPositionPlan ? beam.positionPlanSettings.pdf : undefined,
      )
      if (!textLines.length) return collector

      collector.push({
        guid: beam.guid,
        textLines,
        position: getBeamLabelCenter(beam),
        xDirection: isVertical ? PLAN_VERTICAL_X_DIRECTION.clone() : PLAN_X_DIRECTION.clone(),
        yDirection: isVertical ? PLAN_VERTICAL_Y_DIRECTION.clone() : PLAN_Y_DIRECTION.clone(),
        ...getPdfLabelBoxDescriptorProps(textLines, metrics),
        color,
        opacity: 1,
        textDepth: FLAT_TEXT_DEPTH,
        backgroundColor: '#FFFFFF',
        borderColor: color,
        borderWidth: BEAM_LABEL_BORDER_WIDTH,
      })
      return collector
    }, [])
  }, [
    showBeamLabels,
    showCrossSections,
    isPositionPlan,
    visibleModel.beams,
    guidToColor,
    guidToCrossSection,
    scenePalette.elements2d.beams.color,
    getLabel,
    metrics,
  ])

  // Deckenposition / Dachposition. These replace drei `Html` labels, which are
  // sized in screen pixels; the batched ones are world sized like the beam
  // labels, so they scale with the plan instead of staying constant on zoom.
  const slabPositionDescriptors = useMemo(() => {
    const forType = (
      slabs: (VerticalSlab | VerticalRoofSlab)[],
      show: boolean,
      outlineColor: string,
    ): ElementLabelDescriptor[] => {
      if (!show) return []

      return slabs.reduce((collector: ElementLabelDescriptor[], slab) => {
        // matches the guard in VerticalSlab2DLabelsAndIcons
        if (slab.shape.points.length <= 2) return collector

        const textLines = makePlanSlabLabelLines(
          getLabel(slab.guid),
          elementIdToAssembly[slab.guid],
          showCrossSections,
          isPositionPlan ? slab.positionPlanSettings.pdf : undefined,
        )
        if (!textLines.length) return collector

        collector.push({
          guid: slab.guid,
          textLines,
          position: getSlabLabelCenter(slab, textLines, metrics, storeyTopZ[slab.storey]),
          xDirection: PLAN_X_DIRECTION.clone(),
          yDirection: PLAN_Y_DIRECTION.clone(),
          ...getPdfLabelBoxDescriptorProps(textLines, metrics),
          color: outlineColor,
          opacity: 1,
          textDepth: FLAT_TEXT_DEPTH,
          backgroundColor: '#FFFFFF',
          borderColor: outlineColor,
          borderWidth: BEAM_LABEL_BORDER_WIDTH,
        })

        return collector
      }, [])
    }

    return [
      ...forType(
        visibleModel.vertical_slabs,
        showVerticalSlabLabels,
        scenePalette.elements2d.vertical_slabs.outlineColor as string,
      ),
      ...forType(
        visibleModel.vertical_roof_slabs,
        showVerticalRoofSlabLabels,
        scenePalette.elements2d.vertical_roof_slabs.outlineColor as string,
      ),
    ]
  }, [
    visibleModel.vertical_slabs,
    visibleModel.vertical_roof_slabs,
    showVerticalSlabLabels,
    showVerticalRoofSlabLabels,
    showCrossSections,
    isPositionPlan,
    elementIdToAssembly,
    scenePalette.elements2d.vertical_slabs.outlineColor,
    scenePalette.elements2d.vertical_roof_slabs.outlineColor,
    getLabel,
    metrics,
    storeyTopZ,
  ])

  const purlinDescriptors = useMemo(() => {
    if (!showPurlinLabels) return []

    const color = scenePalette.elements2d.purlins.color as string

    return visibleModel.purlins.reduce((collector: ElementLabelDescriptor[], purlin) => {
      const { start, end } = purlin.shape

      // Deliberately not derived from the purlin direction, which would flip
      // with the order of its start and end points (`purlinV2.isVertical`).
      const isVertical = start.x === end.x
      const textLines = makePlanMemberLabelLines(
        getLabel(purlin.guid),
        guidToCrossSection[purlin.guid],
        showCrossSections,
        isPositionPlan ? purlin.positionPlanSettings.pdf : undefined,
      )
      if (!textLines.length) return collector

      collector.push({
        guid: purlin.guid,
        textLines,
        position: getPurlinLabelCenter(purlin, storeyTopZ[purlin.storey]),
        xDirection: isVertical ? PLAN_VERTICAL_X_DIRECTION.clone() : PLAN_X_DIRECTION.clone(),
        yDirection: isVertical ? PLAN_VERTICAL_Y_DIRECTION.clone() : PLAN_Y_DIRECTION.clone(),
        ...getPdfLabelBoxDescriptorProps(textLines, metrics),
        color,
        opacity: 1,
        textDepth: FLAT_TEXT_DEPTH,
        backgroundColor: '#FFFFFF',
        borderColor: color,
        borderWidth: BEAM_LABEL_BORDER_WIDTH,
      })
      return collector
    }, [])
  }, [
    showPurlinLabels,
    showCrossSections,
    isPositionPlan,
    visibleModel.purlins,
    guidToCrossSection,
    scenePalette.elements2d.purlins.color,
    getLabel,
    metrics,
    storeyTopZ,
  ])

  // The column footprint is a small square crossed by its diagonals, so its
  // label goes above it (top of the plan) instead of on top of it.
  const columnDescriptors = useMemo(() => {
    if (!showColumnLabels) return []

    const defaultColor = scenePalette.elements2d.columns.outlineColor as string

    return visibleModel.columns.reduce((collector: ElementLabelDescriptor[], column) => {
      if (guidToHidden[column.guid]) return collector

      const color = (guidToColor[column.guid] || defaultColor) as string
      const crossSection = guidToCrossSection[column.guid]
      const textLines = makePlanMemberLabelLines(
        getLabel(column.guid),
        crossSection,
        showCrossSections,
        isPositionPlan ? column.positionPlanSettings.pdf : undefined,
      )
      if (!textLines.length) return collector

      collector.push({
        guid: column.guid,
        textLines,
        position: getColumnLabelCenter(column, crossSection, metrics, textLines.length),
        xDirection: PLAN_X_DIRECTION.clone(),
        yDirection: PLAN_Y_DIRECTION.clone(),
        ...getPdfLabelBoxDescriptorProps(textLines, metrics),
        color,
        opacity: 1,
        textDepth: FLAT_TEXT_DEPTH,
        backgroundColor: '#FFFFFF',
        borderColor: color,
        borderWidth: BEAM_LABEL_BORDER_WIDTH,
      })

      return collector
    }, [])
  }, [
    showColumnLabels,
    showCrossSections,
    isPositionPlan,
    visibleModel.columns,
    guidToHidden,
    guidToColor,
    guidToCrossSection,
    scenePalette.elements2d.columns.outlineColor,
    getLabel,
    metrics,
  ])

  return useMemo(
    () => [
      ...wallDescriptors,
      ...stiffeningSegmentDescriptors,
      ...beamDescriptors,
      ...slabPositionDescriptors,
      ...purlinDescriptors,
      ...columnDescriptors,
    ],
    [
      wallDescriptors,
      stiffeningSegmentDescriptors,
      beamDescriptors,
      slabPositionDescriptors,
      purlinDescriptors,
      columnDescriptors,
    ],
  )
}

export default useFloorplanLabelDescriptors
